// src/pages/EmployeeProjectView.tsx
// Refactored with Precision with Warmth design system

import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SidebarPortal } from '../../shared/components/SidebarPortal';
import { ROUTES } from '../../shared/constants/routes';
import { Project, Task, TimeEntry } from '../services/api/types';
import { apiService } from '../services/api';
import { AuthContext } from '../contexts/AuthAdapter';
import { useApi } from '../hooks/useApi';
import TaskCard from '../components/TaskCard';
import ProjectDetailsGrid from '../components/project-form/ProjectDetailsGrid';
import { Button, Input, Spinner, EmptyState } from '../../shared/components/ui';
import './EmployeeProjectView.css';

const EmployeeProjectView: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [todayEntries, setTodayEntries] = useState<TimeEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  // Get project data
  const { data: project, loading: projectLoading, error: projectError } = useApi(
    () => projectId ? apiService.projects.getProjectById(Number(projectId)) : Promise.resolve(null),
    [projectId]
  );

  // Get tasks for this user and project
  const { data: userTasks, loading: tasksLoading } = useApi(
    () => {
      if (user && projectId) {
        return apiService.tasks.getTasksByUserAndProject(user.id, Number(projectId));
      }
      return Promise.resolve([]);
    },
    [user?.id, projectId]
  );

  useEffect(() => {
    if (userTasks && Array.isArray(userTasks)) {
      setTasks(userTasks);
    }
  }, [userTasks]);

  // Fetch time entries when date changes
  useEffect(() => {
    if (user) {
      fetchTodayEntries();
    }
  }, [selectedDate, user?.id]);

  const fetchTodayEntries = async () => {
    if (!user) return;

    setEntriesLoading(true);
    try {
      const entries = await apiService.timeEntries.getDailyTimeEntries(user.id, selectedDate);
      setTodayEntries(Array.isArray(entries) ? entries : []);
    } catch (error) {
      console.error('Error fetching time entries:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        error: error
      });
      setTodayEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleTaskUpdated = () => {
    // Refresh time entries when tasks are updated
    fetchTodayEntries();
  };

  const handleTimeLogged = () => {
    // Refresh time entries when time is logged
    fetchTodayEntries();
  };

  const handleBackToDashboard = () => {
    navigate(ROUTES.QUALITY_MANAGEMENT.EMPLOYEE_DASHBOARD);
  };

  const getTotalHoursForDay = (): number => {
    if (!Array.isArray(todayEntries)) return 0;
    return todayEntries.reduce((total, entry) => total + entry.hours, 0);
  };

  if (projectLoading || tasksLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <Spinner size="lg" />
        <h3 className="font-display text-lg font-semibold text-foreground">Carregando detalhes do projeto...</h3>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <>
        <SidebarPortal>
          <Button variant="primary" fullWidth onClick={handleBackToDashboard}>
            Voltar ao Painel
          </Button>
        </SidebarPortal>
        <EmptyState
          className="py-24"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          }
          title="Erro ao carregar projeto"
          description={projectError?.message || 'Projeto não encontrado'}
          action={
            <Button variant="primary" onClick={handleBackToDashboard}>
              Voltar ao Painel
            </Button>
          }
        />
      </>
    );
  }

  if (!user) {
    return (
      <EmptyState
        className="py-24"
        title="Faça login para ver este projeto"
      />
    );
  }

  return (
    <>
      <SidebarPortal>
        <Button
          variant="primary"
          fullWidth
          className="mb-4"
          onClick={handleBackToDashboard}
          data-testid="back-to-dashboard-button"
          leftIcon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          }
        >
          Voltar ao Painel
        </Button>

        <div className="mb-4 rounded-lg border border-border bg-surface-3 p-4">
          <h4 className="mb-1 text-sm font-medium text-foreground-muted">Visão do Projeto</h4>
          <p className="font-display text-base font-bold text-foreground">{project.name}</p>
          <p className="mt-1 text-xs text-foreground-muted">
            {tasks.length} {tasks.length !== 1 ? 'tarefas atribuídas' : 'tarefa atribuída'}
          </p>
        </div>

        <div className="mb-4 rounded-lg border border-border bg-surface-3 p-4">
          <h4 className="mb-1 text-sm font-medium text-foreground-muted">Progresso de Hoje</h4>
          <p className="font-display text-lg font-bold text-foreground tabular-nums">
            {entriesLoading ? 'Carregando...' : `${getTotalHoursForDay()}h registradas`}
          </p>
          <p className="mt-1 text-xs text-foreground-muted">
            {new Date(selectedDate).toLocaleDateString('pt-BR')}
          </p>
        </div>
      </SidebarPortal>

        {/* Project Header */}
        <div className="border-b border-border bg-surface-1 px-8 py-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl font-bold text-foreground">{project.name}</h1>
              <p className="mt-1 text-base text-foreground-muted">{project.description}</p>
            </div>
            <div className="flex items-center gap-3">
              <label htmlFor="work-date" className="text-sm font-medium text-foreground">Data do trabalho:</label>
              <Input
                id="work-date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                containerClassName="w-auto"
                className="h-9"
                data-testid="work-date-input"
              />
            </div>
          </div>
        </div>

        {/* Project details */}
        <div className="px-8 pt-6">
          <div className="rounded-xl border border-border bg-surface-1 p-6 shadow-xs">
            <h2 className="mb-5 font-display text-base font-semibold text-foreground">Dados do projeto</h2>
            <ProjectDetailsGrid project={project} />
          </div>
        </div>

        {/* Tasks Section */}
        <div className="px-8 py-6">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-display text-xl font-semibold text-foreground">
              Suas Tarefas ({tasks.length})
            </h2>
            <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-2 px-4 py-2">
              <span className="text-sm font-medium text-foreground-muted">Total de hoje:</span>
              <span className="font-display text-base font-bold text-brand tabular-nums">
                {getTotalHoursForDay()}h
              </span>
            </div>
          </div>

          {tasks.length > 0 ? (
            <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(400px,1fr))]">
              {tasks.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onTaskComplete={() => handleTaskUpdated()}
                  onTaskDelete={() => handleTaskUpdated()}
                  onTaskUpdate={() => handleTaskUpdated()}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-surface-2 py-6">
              <EmptyState
                icon={
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                    <path d="M14 2v6h6" />
                  </svg>
                }
                title="Nenhuma tarefa atribuída"
                description="Você ainda não foi atribuído a nenhuma tarefa neste projeto. Fale com seu gestor de projeto para receber tarefas."
              />
            </div>
          )}
        </div>
    </>
  );
};

export default EmployeeProjectView;
